import { DatePicker, Select } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { useEffect, useState, useRef } from 'react';
import FormItem from 'antd/es/form/FormItem';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Basket } from '../../../types/basket';
import dayjs from 'dayjs';
import { disableFutureDates } from '../../../utils/disableDates';
import { normalize } from '../../../utils/normalizeWords';
import { getEstablishmentPriceBreakdownData } from '../getProductPriceAnalyticsData';
import { downloadCustomCSV } from '../../../helpers/utilityHelper';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { sanitizeInput } from '../../../utils/sanitizeInput';

interface EstablishmentPriceVariationChartProps {
  t: (key: string) => string;
}

const EstablishmentPriceVariationChart = ({ t }: EstablishmentPriceVariationChartProps) => {
  const today = dayjs();
  // const oneYearAgo = today.subtract(1, 'year');
  const oneMonthAgo = today.subtract(1, 'month');

  const [filterLoading, setFilterLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [presentations, setPresentations] = useState<any>([]);
  const [selectedBasket, setSelectedBasket] = useState<number | undefined>(undefined);
  const [selectedPresentations, setSelectedPresentations] = useState<string[]>([]);
  const [selectedTimeInterval, setSelectedTimeInterval] = useState<
    'daily' | 'weekly' | 'monthly' | 'yearly'
  >('daily');
  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
    oneMonthAgo,
    today,
  ]);

  const [establishmentTypes, setEstablishmentTypes] = useState<{label: string, value: string}[]>([]);
  const [establishments, setEstablishments] = useState<string[]>([]);
  const [selectedEstablishmentType, setSelectedEstablishmentType] = useState<string[]>([]);
  const [selectedEstablishments, setSelectedEstablishments] = useState<string[]>([]);
  const [selectedEstablishmentIds, setSelectedEstablishmentIds] = useState<number[]>([]);

  const { isOnline } = useIsOnlineContext();

  const [establishmentData, setEstablishmentData] = useState<
    Array<{ positionType: string; name: string; id: number }>
  >([]);
  const [isChartdataEmpty, setIsChardataEmpty] = useState<boolean>(false);
  const [chartData, setChartData] = useState<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }>({
    categories: [],
    series: [],
    timeInterval: 'daily',
  });

  const filtersRef = useRef({
    basket: {} as { label: string; value: number },
    presentations: [] as { label: string; value: string }[],
    establishmentType: [] as string[],
    establishmentName: [] as string[],
  });

  const chartId = 'chartTitlePriceVariationByEstablishment';

  const basketItems = [
    ...baskets.map((basket) => ({
      label: basket.name,
      value: basket.id,
    })),
  ];

  const sideFilters = [
    { label: t('daily'), value: 'daily' },
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
    { label: t('yearly'), value: 'yearly' },
  ];

  const handleBasketChange = (value: any) => {
    setSelectedBasket(value);
    setSelectedPresentations([]);
  };

  const loadPresentationsForSelection = async () => {
    try {
      const allPresentations = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket || undefined
      );

      const presentationOptionArr = allPresentations.map((presentation: any) => ({
        label: presentation?.name,
        value: presentation?.refId,
        id: presentation?.refId,
      }));

      setPresentations(presentationOptionArr);
    } catch (error) {
      console.error('Error loading presentations for selection:', error);
      setPresentations([]);
    }
  };

  useEffect(() => {
    loadPresentationsForSelection();
  }, [selectedBasket]);

  const handleTimeIntervalChange = (filter: string | number) => {
    setSelectedTimeInterval(filter as 'daily' | 'weekly' | 'monthly' | 'yearly');
  };

  const fetchChartData = async () => {
    if (!selectedBasket) {
      return;
    }

    try {
      setLoading(true);
      const params = {
        basketId: selectedBasket || undefined,
        productRefIds: selectedPresentations || undefined,
        startTime: selectedDateRange && selectedDateRange[0].startOf('day').valueOf(),
        endTime: selectedDateRange && selectedDateRange[1].endOf('day').valueOf(),
        timeInterval: selectedTimeInterval,
        timezoneOffset: new Date().getTimezoneOffset(),
        establishmentTypes: selectedEstablishmentType && selectedEstablishmentType.length > 0 ? selectedEstablishmentType : undefined,
        establishmentIds: selectedEstablishments && selectedEstablishments.length > 0 && selectedEstablishmentIds && selectedEstablishmentIds.length > 0 ? selectedEstablishmentIds : undefined,
      };

      const queryParams: any = {};

      if (params.basketId) queryParams.basketId = params.basketId;
      if (
        params?.productRefIds &&
        Array.isArray(params?.productRefIds) &&
        params?.productRefIds.length > 0
      ) {
        queryParams.productRefIds = params?.productRefIds;
      }
      if (params.startTime) queryParams.startTime = params.startTime.toString();
      if (params.endTime) queryParams.endTime = params.endTime.toString();
      if (params.timeInterval) queryParams.timeInterval = params.timeInterval;
      if (params.timezoneOffset) queryParams.timezoneOffset = params.timezoneOffset.toString();
      if (params.establishmentTypes) queryParams.establishmentTypes = params.establishmentTypes.toString();
      if (params.establishmentIds) queryParams.establishmentIds = params.establishmentIds.toString();

      const data = await getEstablishmentPriceBreakdownData(queryParams);
      const dataAvailailityCheck =
        data && data?.series?.every((item: any) => item?.data?.length === 0);
      setIsChardataEmpty(dataAvailailityCheck);
      setChartData(data);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setIsChardataEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setFilterLoading(true);
      try {
        const basketsData = await referenceDataSyncService.getBaskets();
        setBaskets(basketsData as unknown as Basket[]);
        if (selectedBasket == null) {
          setSelectedBasket(await referenceDataSyncService.getBasicFoodBasketId());
        }

        const establishmentData = await referenceDataSyncService.getEstablishments();
        const formattedEstablishmentData = establishmentData.map((establishment: any) => ({
          positionType: (establishment.establishmentType?.key || establishment.establishmentType?.nameEn || establishment.establishmentType || '') as string,
          name: establishment.name as string,
          id: establishment.id as number,
        }));
        setEstablishmentData(formattedEstablishmentData);
        setEstablishments(formattedEstablishmentData);
        
        const uniqueTypesMap = new Map<string, string>();
        establishmentData.forEach((est: any) => {
          const typeObj = est.establishmentType;
          if (typeObj && typeof typeObj === 'object' && typeObj.key) {
            uniqueTypesMap.set(typeObj.key, typeObj.nameEn || typeObj.key);
          } else if (typeof typeObj === 'string' && typeObj) {
            uniqueTypesMap.set(typeObj, typeObj);
          }
        });
        setEstablishmentTypes(
          Array.from(uniqueTypesMap.entries()).map(([key, name]) => ({
            label: name,
            value: key,
          }))
        );
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setFilterLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [
    selectedBasket,
    selectedPresentations,
    selectedEstablishmentIds,
    selectedTimeInterval,
    selectedDateRange,
  ]);

  useEffect(() => {
    if (selectedEstablishmentType) {
      const establishmentsForSelectedType = establishmentData
        .filter(
          (establishment) =>
            establishment?.name !== 'Other' &&
            selectedEstablishmentType.includes(establishment?.positionType)
        )
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentsForSelectedType);

      const filteredEstablishments = establishmentData
        .filter(
          (establishment) =>
            establishment?.name !== 'Other' &&
            selectedEstablishmentType.includes(establishment?.positionType)
        )
        .map((establishment) => establishment.name);

      const establishmentWithoutDuplicates = Array.from(new Set(filteredEstablishments));

      setEstablishments(establishmentWithoutDuplicates);
      setSelectedEstablishments([]);
    } else {
      setEstablishments([]);
      setSelectedEstablishments([]);
      setSelectedEstablishmentIds([]);
    }
  }, [selectedEstablishmentType, establishmentData]);

  useEffect(() => {
    if (selectedEstablishments?.length > 0 && selectedEstablishmentType?.length > 0) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter(
          (establishment) =>
            selectedEstablishmentType.includes(establishment.positionType) &&
            selectedEstablishments.includes(establishment.name)
        )
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    } else if (selectedEstablishments?.length > 0) {
      const establishmentIdsForSelectedEstablishment = establishmentData
        .filter((establishment) => selectedEstablishments.includes(establishment.name))
        .map((establishment) => establishment.id);

      setSelectedEstablishmentIds(establishmentIdsForSelectedEstablishment);
    }
  }, [selectedEstablishments]);

  const downloadPNG = async () => {
    const dataURI = await ApexCharts.exec(chartId, 'dataURI');

    const link = document.createElement('a');
    link.href = dataURI.imgURI;
    link.download = `${t(chartId)}_${Date.now()}.png`;
    link.click();

    await ApexCharts.exec(
      chartId,
      'updateOptions',
      {
        xaxis: { min: currentZoom.current.min, max: currentZoom.current.max },
        annotations: {
          texts: [],
        },
      },
      false,
      true
    );
  };

  const currentZoom = useRef({ min: null, max: null });

  return (
    <div className="chart">
      <section className="mb-2 mt-2 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h3 className="text-2xl font-semibold">{t('priceVariationByEstablishment')}</h3>
        </div>
        {!isChartdataEmpty && (
          <div className="flex justify-end gap-3 text-sm md:gap-4">
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                setIsDownloading(true);
                try {
                  downloadPNG();
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadPNG')}
            </PrimaryButton>
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;

                if (!filtersRef.current?.basket?.label) {
                  const option = basketItems.find((o) => o.value === selectedBasket);
                  filtersRef.current.basket = option as any;
                }

                const filterData = [
                  {
                    key: 'basket',
                    label: t('downloadFilters.basket'),
                    value: filtersRef.current.basket?.label,
                  },
                  {
                    key: 'presentations',
                    label: t('presentations'),
                    value: filtersRef.current.presentations.map((a) => a.label),
                  },
                  {
                    key: 'establishmentType',
                    label: t('downloadFilters.establishmentType'),
                    value: filtersRef.current.establishmentType,
                  },
                  {
                    key: 'establishmentName',
                    label: t('downloadFilters.establishmentName'),
                    value: filtersRef.current.establishmentName,
                  },
                  {
                    key: 'dateRange',
                    label: t('downloadFilters.dateRange'),
                    value: `[${selectedDateRange?.[0]?.format('YYYY-MM-DD')},${selectedDateRange?.[1]?.format('YYYY-MM-DD')}]`,
                  },
                  {
                    key: 'timeInterval',
                    label: t('timeInterval'),
                    value: t(`${selectedTimeInterval}`),
                  },
                ];

                setIsDownloading(true);
                try {
                  downloadCustomCSV(
                    t('chartTitlePriceVariationByEstablishment'),
                    chartData,
                    filterData,
                    t,
                    t('date')
                  );
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </div>
        )}
      </section>

      {/* <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3> */}
      {/* <p className='mb-2 w-full text-left lg:w-auto'>{t('filters')}:</p> */}
      <div className="mb-6 mt-4 grid w-full grid-cols-1 items-start justify-between gap-6 overflow-visible md:grid-cols-4 lg:grid-cols-5 lg:items-center">
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('basket')}</span>}
        >
          <Select
            value={selectedBasket}
            options={basketItems}
            placeholder={t('basketPlaceholder')}
            showSearch
            onChange={(val, option) => {
              filtersRef.current.basket = option as any;
              handleBasketChange(val);
            }}
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('presentations')}</span>}
        >
          <Select
            value={selectedPresentations}
            options={presentations}
            placeholder={t('presentationsPlaceholder')}
            allowClear
            onClear={() => setSelectedPresentations([])}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            onChange={(vals, options) => {
              filtersRef.current.presentations = options as any;
              setSelectedPresentations(vals);
            }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={
            <span className="text-sm font-medium text-gray-700">{t('establishmentType')}</span>
          }
        >
          <Select
            value={selectedEstablishmentType}
            options={establishmentTypes}
            placeholder={t('establishmentTypePlaceholder')}
            allowClear
            mode="multiple"
            onClear={() => setSelectedEstablishmentType([])}
            showSearch
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            onChange={(val) => {
              filtersRef.current.establishmentType = val;
              setSelectedEstablishmentType(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
          />
        </FormItem>
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('establishment')}</span>}
        >
          <Select
            value={selectedEstablishments}
            options={establishments.map((establishment) => ({
              label: establishment,
              value: establishment,
            }))}
            placeholder={t('establishmentPlaceholder')}
            allowClear
            onClear={() => setSelectedEstablishments([])}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            disabled={!selectedEstablishmentType || establishments.length === 0}
            onChange={(val) => {
              filtersRef.current.establishmentName = val;
              setSelectedEstablishments(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
          />
        </FormItem>
        <FormItem
          className="relative col-span-2 mb-0 w-full 3xl:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('dateFilter')}</span>}
        >
          <DatePicker.RangePicker
            className="mt-2 w-full rounded-lg px-4 py-2 md:mt-0"
            value={selectedDateRange}
            onChange={(dates) => setSelectedDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            format="YYYY-MM-DD"
            placeholder={[t('from'), t('to')]}
            disabledDate={disableFutureDates}
            allowClear={false}
          />
        </FormItem>
      </div>

      <div className="flex flex-col justify-end lg:flex-row">
        <div className="mt-4 lg:mt-0">
          <Select
            popupClassName="!w-[150px]"
            className="w-[max(fit-content,120px)] [&>.ant-select-selector]:!border-none [&>.ant-select-selector]:!pl-0 [&_.ant-select-selection-item]:!text-base [&_.ant-select-selection-item]:!text-black [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!text-black [&_.ant-select-selection-placeholder]:hover:!text-black/50 [&_input]:!capitalize"
            options={sideFilters.map((filter) => ({
              label: filter.label,
              value: filter.value,
            }))}
            defaultValue={sideFilters[0].value}
            placeholder={t('select')}
            onChange={(value) => handleTimeIntervalChange(value)}
            value={selectedTimeInterval}
          />
        </div>
      </div>

      {isChartdataEmpty ? (
        <div key="no-data" className="mb-6 py-8 text-center text-sm text-gray-500">
          {t('noDataAvailable')}
        </div>
      ) : (
        <ChartComponent
          title={''}
          type={CHART_TYPES.LINE}
          series={chartData?.series?.map((s) => ({ ...s, name: sanitizeInput(s?.name) }))}
          options={{
            chart: {
              id: chartId,
              toolbar: {
                show: true,
                tools: {
                  download: false,
                  selection: true,
                  zoom: true,
                  zoomin: true,
                  zoomout: true,
                  pan: false,
                  reset: true,
                },
              },
              events: {
                zoomed(_chart, { xaxis }) {
                  currentZoom.current = xaxis;
                },
              },
              zoom: {
                enabled: true,
              },
            },
            stroke: {
              curve: 'smooth',
              width: 2,
            },
            markers: {
              size: 4,
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories: chartData?.categories?.map(sanitizeInput),
            },
            yaxis: {
              title: {
                text: t('priceYaxis'),
              },
            },
            colors: ['#00b050', '#ff0000', '#0070c0'],
            legend: {
              position: 'bottom',
              showForSingleSeries: true,
            },
            grid: {
              borderColor: '#e7e7e7',
              strokeDashArray: 4,
            },
            fill: {
              type: 'gradient',
              gradient: {
                gradientToColors: ['#A8D0F3', '#E0E7F5'],
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.5,
                stops: [0, 90, 100],
              },
            },
          }}
          /* sideFilters={[
          { label: t('daily'), value: 'daily' },
          { label: t('weekly'), value: 'weekly' },
          { label: t('monthly'), value: 'monthly' },
          { label: t('yearly'), value: 'yearly' },
        ]}
        handleSideFiltersChange={handleTimeIntervalChange}
        selectedSideFilter={selectedTimeInterval} */
          maxHeight={400}
          loading={loading || filterLoading}
        />
      )}
    </div>
  );
};

export default EstablishmentPriceVariationChart;
